import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import MapGL, { Source, Layer, Popup, MapRef } from "react-map-gl/maplibre";
import { Typography, Box } from "@mui/material";
import type { MapLayerMouseEvent } from "maplibre-gl";
import { Map as MapIcon } from "@mui/icons-material";
import "maplibre-gl/dist/maplibre-gl.css";

import type { JobMapProps } from "./JobMap.types";
import { DEFAULT_CENTER, DEFAULT_ZOOM } from "./JobMap.types";
import { useJobMapViewModel } from "./JobMap.vm";
import { JobMapToolbar } from "./JobMap.toolbar";
import {
	polygonFillLayer,
	polygonLineLayer,
	pointCircleLayer,
	pointLabelLayer,
	hatchLineLayer,
	nearbyFillLayer,
	nearbyLineLayer,
	unmatchedPointsLayer,
	unmatchedZonesFillLayer,
	unmatchedZonesLineLayer,
	duplicatePointsLayer,
	duplicateZonesFillLayer,
	duplicateZonesLineLayer,
	highlightFillLayer,
	highlightLineLayer,
} from "./layerSpecs";
import { useMapDrawEngine } from "../../map/engine/useMapDrawEngine";
import { parsePolygonId } from "./editHelpers";
import { MapEditProvider, useMapEdit } from "../../map/context/MapEditContext";
import { MapToolProvider, useMapTool } from "../../map/context/MapToolContext";
import { ToolOverlayLayer, useToolOverlay } from "../../map/layers/ToolOverlayLayer";
import { useToolRegistration } from "../../map/tools/useToolRegistration";
import { LassoTypeDialog } from "../../map/tools/LassoTypeDialog";
import type { LassoToolConfig } from "../../map/tools/useLassoTool";
import type { LassoFeatureType } from "../../../application/map/tools/types";
import { TileSourceControl } from "./TileSourceControl";

// ── Tool registrar (null component) ─────────────────────────────────────────

function ToolRegistrar({ lassoConfig }: { lassoConfig?: LassoToolConfig }) {
	useToolRegistration(lassoConfig);
	return null;
}

// ── Undo keyboard handler (Ctrl+Z / Ctrl+Shift+Z / Ctrl+Y) ─────────────────

function UndoKeyboardHandler() {
	const { undoStack } = useMapEdit();
	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			const mod = e.ctrlKey || e.metaKey;
			if (!mod) return;
			if (e.key === "z" && !e.shiftKey) {
				e.preventDefault();
				undoStack.undo();
			} else if (e.key === "Z" || e.key === "y") {
				e.preventDefault();
				undoStack.redo();
			}
		};
		window.addEventListener("keydown", handler);
		return () => window.removeEventListener("keydown", handler);
	}, [undoStack]);
	return null;
}

// ── Empty state ─────────────────────────────────────────────────────────────

function EmptyState({ height }: { height?: string | number }) {
	return (
		<Box
			sx={{
				height: height ?? 200,
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				bgcolor: "grey.50",
				borderRadius: height === "100%" ? 0 : 1,
				mt: height === "100%" ? 0 : 2,
				border: 1,
				borderColor: "divider",
				gap: 1,
			}}
		>
			<MapIcon sx={{ fontSize: 48, color: "text.disabled" }} />
			<Typography variant="body2" color="text.secondary">
				No data to display
			</Typography>
		</Box>
	);
}

// ── Main component ──────────────────────────────────────────────────────────

export default function JobMap(props: JobMapProps) {
	return (
		<MapToolProvider>
			<MemoJobMapInner {...props} />
		</MapToolProvider>
	);
}

const MemoJobMapInner = memo(JobMapInner);

function JobMapInner(props: JobMapProps) {
	const mapRef = useRef<MapRef>(null);
	const [mapLoaded, setMapLoaded] = useState(false);
	const lastFitKey = useRef<string | null>(null);
	const toolOverlay = useToolOverlay();
	const { state: toolState, activate, deactivate, getRegistered } = useMapTool();
	const vm = useJobMapViewModel(props, { activeTool: toolState.activeTool });

	const editable = !!props.onGeometryEdit;

	const drawEngine = useMapDrawEngine({
		mapRef,
		enabled: mapLoaded && (editable || !!props.showToolbar || !!props.lassoMode),
	});

	// ── Lasso type dialog state ─────────────────────────────────────────
	const [lassoTypePickerState, setLassoTypePickerState] = useState<{
		availableTypes: LassoFeatureType[];
		onSelect: (type: LassoFeatureType) => void;
		onCancel: () => void;
	} | null>(null);

	const requestFeatureType = useCallback(
		(types: LassoFeatureType[], onSelect: (t: LassoFeatureType) => void, onCancel: () => void) => {
			setLassoTypePickerState({ availableTypes: types, onSelect, onCancel });
		},
		[],
	);

	// ── Lasso config ────────────────────────────────────────────────────

	const lassoConfig = useMemo<LassoToolConfig>(
		() => ({
			getLassoMetadata: vm.getLassoMetadata,
			onLassoComplete: vm.onLassoComplete,
			requestFeatureType: editable ? requestFeatureType : undefined,
		}),
		[vm.getLassoMetadata, vm.onLassoComplete, editable, requestFeatureType],
	);

	// ── lassoMode → tool activation ──────────────────────────────────────
	useEffect(() => {
		if (vm.effectiveLassoMode && toolState.activeTool !== "lasso") {
			activate("lasso");
		} else if (!vm.effectiveLassoMode && toolState.activeTool === "lasso") {
			deactivate();
		}
	}, [vm.effectiveLassoMode, toolState.activeTool, activate, deactivate]);

	// ── Fit bounds (once per unique job set) ────────────────────────────
	useEffect(() => {
		if (!mapLoaded || !vm.bounds || !mapRef.current) return;
		if (lastFitKey.current === vm.jobIdKey) return;
		const padding = vm.boundsPadding
			? {
					top: vm.boundsPadding.top ?? 40,
					bottom: vm.boundsPadding.bottom ?? 40,
					left: vm.boundsPadding.left ?? 40,
					right: vm.boundsPadding.right ?? 40,
				}
			: 40;
		mapRef.current.fitBounds(vm.bounds, { padding, maxZoom: 16 });
		lastFitKey.current = vm.jobIdKey;
	}, [mapLoaded, vm.bounds, vm.boundsPadding, vm.jobIdKey]);

	// ── Double-click → enter vertex editing ─────────────────────────────
	const handleDblClick = useCallback(
		(e: MapLayerMouseEvent) => {
			if (!editable) return;
			// Prevent MapLibre's default double-click-to-zoom
			e.preventDefault();
			if (toolState.activeTool) {
				const tool = getRegistered(toolState.activeTool);
				if (tool?.onMapDblClick) {
					tool.onMapDblClick({
						lngLat: { lng: e.lngLat.lng, lat: e.lngLat.lat },
						point: { x: e.point.x, y: e.point.y },
					});
				}
				return;
			}
			if (e.features && e.features.length > 0) {
				activate("edit-vertices");
			}
		},
		[editable, toolState.activeTool, activate, getRegistered],
	);

	// ── Click handler ───────────────────────────────────────────────────
	const handleClick = useCallback(
		(e: MapLayerMouseEvent) => {
			// While editing vertices, don't change selection
			if (toolState.activeTool === "edit-vertices") return;

			const activeTool = toolState.activeTool ? getRegistered(toolState.activeTool) : null;
			const mapClickEvent = {
				lngLat: { lng: e.lngLat.lng, lat: e.lngLat.lat },
				point: { x: e.point.x, y: e.point.y },
			};

			if (e.features && e.features.length > 0) {
				const feature = e.features[0];
				const featureId = feature.properties?.id as string;
				const jobId = feature.properties?.jobId as string;

				if (activeTool?.onFeatureClick) {
					// Only dispatch to tool if clicked feature belongs to the selected job
					if (jobId === vm.selectedJobId) {
						const clickId = (feature.properties?.sampleId as string) ?? featureId;
						activeTool.onFeatureClick(clickId, mapClickEvent);
					}
				} else if (activeTool?.onMapClick) {
					activeTool.onMapClick(mapClickEvent);
				} else {
					// Auto-activate point-modify when clicking a point on the selected job
					const sampleId = feature.properties?.sampleId as string | undefined;
					if (sampleId && editable && jobId === vm.selectedJobId) {
						activate("point-modify");
						getRegistered("point-modify")?.onFeatureClick?.(sampleId, mapClickEvent);
					} else {
						// Default: select polygon + derive job
						if (featureId) vm.setSelectedPolygonId(featureId);
						if (jobId) {
							vm.setSelectedJobId(jobId);
						} else if (featureId) {
							const parsed = parsePolygonId(featureId);
							if (parsed) vm.setSelectedJobId(parsed.jobId);
						}
						if (!vm.showToolbar) vm.onPolygonClick(e);
					}
				}
			} else {
				vm.setSelectedPolygonId(null);
				if (activeTool?.onMapClick) {
					activeTool.onMapClick(mapClickEvent);
				} else {
					vm.onMapClick(e);
				}
			}
		},
		[
			toolState.activeTool,
			getRegistered,
			vm.onPolygonClick,
			vm.onMapClick,
			editable,
			vm.selectedJobId,
			activate,
			vm.setSelectedJobId,
			vm.setSelectedPolygonId,
			vm.showToolbar,
		],
	);

	// ── Save / Cancel edit (delegates to edit-vertices tool) ────────────
	const handleSave = useCallback(() => {
		const tool = getRegistered("edit-vertices");
		if (tool && "save" in tool) {
			(tool as any).save();
		}
		deactivate();
		vm.setSelectedPolygonId(null);
	}, [getRegistered, deactivate, vm.setSelectedPolygonId]);

	const handleCancel = useCallback(() => {
		deactivate();
		vm.setSelectedPolygonId(null);
	}, [deactivate, vm.setSelectedPolygonId]);

	// ── Cursor ──────────────────────────────────────────────────────────
	// Terra-draw resets canvas.style.cursor on mode changes. React won't re-apply
	// the cursor prop if the value is unchanged between renders (e.g. "crosshair"
	// → "crosshair" on tool switch). Force-apply whenever the active tool changes.
	useEffect(() => {
		const canvas = mapRef.current?.getMap()?.getCanvas();
		if (canvas) canvas.style.cursor = vm.cursor ?? "";
	}, [toolState.activeTool, vm.cursor]);

	// ── Empty state (skip in edit mode — user needs map for undo/redraw) ──
	if (vm.showEmptyState && !vm.hasGeodata && !editable) {
		return <EmptyState height={vm.resolvedHeight} />;
	}

	return (
		<MapEditProvider
			drawEngine={drawEngine}
			toolOverlay={toolOverlay}
			selectedJobId={vm.selectedJobId}
			selectedPolygonId={vm.selectedPolygonId}
			getJobData={vm.getJobData}
			onApply={vm.onApply}
			onError={vm.onError}
		>
			<ToolRegistrar lassoConfig={lassoConfig} />
			{editable && <UndoKeyboardHandler />}

			<Box sx={{ width: "100%", height: vm.resolvedHeight ?? "100%", display: "flex", flexDirection: "column" }}>
				{vm.showToolbar && (
					<JobMapToolbar
						vm={vm}
						editable={editable}
						toolbarActions={props.toolbarActions}
						onClose={props.onClose}
						selectedJobId={vm.selectedJobId}
						onClearSelectedJob={() => vm.setSelectedJobId(null)}
						jobs={vm.effectiveJobs}
						onSave={handleSave}
						onCancel={handleCancel}
					/>
				)}

				<Box sx={{ flex: 1, minHeight: 0, position: "relative" }}>
					<MapGL
						ref={mapRef}
						mapStyle={vm.tileSource.style}
						initialViewState={{
							longitude: DEFAULT_CENTER.lng,
							latitude: DEFAULT_CENTER.lat,
							zoom: DEFAULT_ZOOM,
						}}
						style={{ width: "100%", height: "100%" }}
						onLoad={() => setMapLoaded(true)}
						onClick={handleClick}
						onDblClick={handleDblClick}
						interactiveLayerIds={["polygon-fill", "point-circles"]}
						cursor={vm.cursor}
						cooperativeGestures={vm.cooperativeGestures}
					>
						{/* Standard layers */}
						<Source id="polygons" type="geojson" data={vm.displayPolygonFC}>
							<Layer {...polygonFillLayer} />
							<Layer {...polygonLineLayer} />
						</Source>

						<Source id="points" type="geojson" data={vm.pointFC}>
							<Layer {...pointCircleLayer(vm.zoomLevelToDisplayPoints)} />
							<Layer {...pointLabelLayer(vm.zoomLevelToDisplayPoints)} />
						</Source>

						{/* Wizard layers — only render when non-empty */}
						{vm.hatchLinesFC.features.length > 0 && (
							<Source id="hatch-lines" type="geojson" data={vm.hatchLinesFC}>
								<Layer {...hatchLineLayer} />
							</Source>
						)}

						{vm.nearbyJobsFC.features.length > 0 && (
							<Source id="nearby-jobs" type="geojson" data={vm.nearbyJobsFC}>
								<Layer {...nearbyFillLayer} />
								<Layer {...nearbyLineLayer} />
							</Source>
						)}

						{vm.unmatchedPointsFC.features.length > 0 && (
							<Source id="unmatched-points" type="geojson" data={vm.unmatchedPointsFC}>
								<Layer {...unmatchedPointsLayer} />
							</Source>
						)}

						{vm.unmatchedZonesFC.features.length > 0 && (
							<Source id="unmatched-zones" type="geojson" data={vm.unmatchedZonesFC}>
								<Layer {...unmatchedZonesFillLayer} />
								<Layer {...unmatchedZonesLineLayer} />
							</Source>
						)}

						{vm.duplicatePointsFC.features.length > 0 && (
							<Source id="duplicate-points" type="geojson" data={vm.duplicatePointsFC}>
								<Layer {...duplicatePointsLayer} />
							</Source>
						)}

						{vm.duplicateZonesFC.features.length > 0 && (
							<Source id="duplicate-zones" type="geojson" data={vm.duplicateZonesFC}>
								<Layer {...duplicateZonesFillLayer} />
								<Layer {...duplicateZonesLineLayer} />
							</Source>
						)}

						{vm.highlightFC.features.length > 0 && (
							<Source id="highlight" type="geojson" data={vm.highlightFC}>
								<Layer {...highlightFillLayer} />
								<Layer {...highlightLineLayer} />
							</Source>
						)}

						{/* Edit tool overlay */}
						<ToolOverlayLayer overlay={toolOverlay} />

						{/* Popup */}
						{vm.popup && (
							<Popup
								longitude={vm.popup.lng}
								latitude={vm.popup.lat}
								onClose={vm.closePopup}
								closeOnClick={false}
								anchor="bottom"
							>
								<Typography variant="body2">
									<strong>
										{vm.popup.job.importFieldName || vm.popup.job.fieldName || vm.popup.job.fieldId || "Unnamed Field"}
									</strong>
									{(vm.popup.job.importFarmName || vm.popup.job.farmName) && (
										<>
											<br />
											Farm: {vm.popup.job.importFarmName || vm.popup.job.farmName}
										</>
									)}
									{(vm.popup.job.importGrowerName || vm.popup.job.growerName) && (
										<>
											<br />
											Grower: {vm.popup.job.importGrowerName || vm.popup.job.growerName}
										</>
									)}
								</Typography>
							</Popup>
						)}
					</MapGL>

					<TileSourceControl tileSources={vm.tileSources} value={vm.tileSource.id} onChange={vm.setTileSourceId} />
				</Box>
			</Box>

			{/* Lasso type picker dialog */}
			{lassoTypePickerState && (
				<LassoTypeDialog
					open={true}
					availableTypes={lassoTypePickerState.availableTypes}
					onSelect={(type) => {
						lassoTypePickerState.onSelect(type);
						setLassoTypePickerState(null);
					}}
					onCancel={() => {
						lassoTypePickerState.onCancel();
						setLassoTypePickerState(null);
					}}
				/>
			)}
		</MapEditProvider>
	);
}
